import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Wallet, PiggyBank, Receipt, Gauge } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

function Cell({ c, testId }) {
  if (!c.count) return <td className="p-2 text-center text-[10px] text-slate-300 font-mono-plex" data-testid={testId}>—</td>;
  const over = c.budget > 0 && c.spent > c.budget;
  const pct = c.budget > 0 ? Math.min(100, Math.round((c.spent / c.budget) * 100)) : 100;
  return (
    <td className="p-2 align-top" data-testid={testId}>
      <div className={`text-xs font-mono-plex font-bold ${over ? "text-rose-700" : "text-slate-900"}`}>{money(c.spent)}</div>
      <div className="text-[10px] font-mono-plex text-slate-500">of {money(c.budget)}</div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${over ? "bg-rose-500" : pct >= 85 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
      {over && <div className="text-[9px] text-rose-700 font-mono-plex">+{money(c.spent - c.budget)} over</div>}
    </td>
  );
}

export default function MarketingBudget() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [gran, setGran] = useState("month");
  const [view, setView] = useState("facility");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/marketing/budget", { params: { year } }).then(({ data }) => setData(data)).catch(() => setData(null)); }, [year]);
  if (!data) return null;
  const t = data.total;
  const rows = view === "facility" ? data.by_facility : data.by_rep;
  const cols = gran === "month" ? MONTHS : QUARTERS;
  const pick = (b) => gran === "month" ? b.months : b.quarters;
  const util = t.utilization_pct;
  const tiles = [
    { l: "Annual budget", v: money(t.budget), grad: "from-indigo-500 to-violet-600", I: Wallet, sub: `${t.count} activities` },
    { l: "Spent to date", v: money(t.spent), grad: "from-orange-400 to-rose-500", I: Receipt, sub: `${money(t.revenue)} revenue back` },
    { l: "Remaining", v: `${t.remaining < 0 ? "-" : ""}${money(Math.abs(t.remaining))}`, grad: t.remaining < 0 ? "from-rose-600 to-pink-700" : "from-emerald-500 to-teal-600", I: PiggyBank, sub: t.remaining < 0 ? "over budget" : "left to invest" },
    { l: "Utilization", v: util == null ? "—" : `${util}%`, grad: util != null && util > 100 ? "from-rose-500 to-pink-600" : "from-sky-500 to-cyan-500", I: Gauge, sub: util == null ? "no budget set" : util > 100 ? "over plan" : "of budget used" },
  ];
  return (
    <Card className="border-slate-200 shadow-none overflow-hidden" data-testid="marketing-budget">
      <div className="p-5 border-b border-slate-100 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Marketing budget consolidation</div>
          <h3 className="font-heading font-bold text-xl">Budget vs spent · {data.year}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Rolled up from every facility's Marketing tab — by facility or by rep, monthly or quarterly.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={year} onValueChange={setYear}><SelectTrigger className="w-24 h-9" data-testid="budget-year"><SelectValue /></SelectTrigger><SelectContent>{[...new Set([...data.years, String(new Date().getFullYear())])].sort().reverse().map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select>
          <Tabs value={gran} onValueChange={setGran}><TabsList><TabsTrigger value="month" data-testid="budget-gran-month">Monthly</TabsTrigger><TabsTrigger value="quarter" data-testid="budget-gran-quarter">Quarterly</TabsTrigger></TabsList></Tabs>
          <Tabs value={view} onValueChange={setView}><TabsList><TabsTrigger value="facility" data-testid="budget-view-facility">By facility</TabsTrigger><TabsTrigger value="rep" data-testid="budget-view-rep">By rep</TabsTrigger></TabsList></Tabs>
        </div>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 p-4" data-testid="budget-tiles">
        {tiles.map(({ l, v, grad, I, sub }) => (
          <div key={l} className={`relative overflow-hidden rounded-xl p-4 text-white bg-gradient-to-br ${grad} shadow-md`} data-testid={`budget-tile-${l.split(" ")[0].toLowerCase()}`}>
            <I className="absolute -right-3 -bottom-3 w-20 h-20 text-white/15" />
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/85">{l}</div>
            <div className="font-heading font-black text-3xl mt-1 drop-shadow-sm">{v}</div>
            <div className="text-[11px] font-mono-plex text-white/85 mt-1">{sub}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="budget-table">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">
            <tr><th className="p-2.5 sticky left-0 bg-slate-50">{view === "facility" ? "Facility" : "Rep"}</th>{cols.map(c => <th key={c} className="p-2.5">{c}</th>)}<th className="p-2.5">Year</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length + 2} className="p-6 text-center text-slate-400 text-sm">No marketing budgets logged for {data.year}. Add activities with a budget on a facility's Marketing tab.</td></tr>}
            {rows.map(b => (
              <tr key={b.facility_id || b.rep_id} className="border-t border-slate-100" data-testid={`budget-row-${b.facility_id || b.rep_id}`}>
                <td className="p-2.5 sticky left-0 bg-white font-medium text-slate-900 whitespace-nowrap">{b.facility_id ? <Link to={`/facilities/${b.facility_id}`} className="hover:underline">{b.name}</Link> : (b.name || "—")}<div className="text-[10px] text-slate-500 font-mono-plex">{b.count} activities · {b.utilization_pct == null ? "no budget" : `${b.utilization_pct}% used`}</div></td>
                {pick(b).map((c, i) => <Cell key={i} c={c} testId={`budget-cell-${b.facility_id || b.rep_id}-${i}`} />)}
                <Cell c={b} />
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="budget-total-row">
                <td className="p-2.5 sticky left-0 bg-slate-50">Total</td>
                {pick(t).map((c, i) => <Cell key={i} c={c} />)}
                <Cell c={t} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
